import { computed, watch } from "vue";
import orderBy from "lodash.orderby";
import get from "lodash.get";
import debounce from "lodash.debounce";
import deepEqual from "fast-deep-equal";
import { useRoute } from "vue-router/composables";
import store from "@/store";
import FILTER_RULES from "@/constants/filterRules";
import SHORTCUTS from "@/constants/shortcuts";

export default function useFilterUtils() {
  const route = useRoute();

  const filterId = computed(() => route.query.filterId);

  const currentFilter = computed(() => {
    return store.getters["filters/getFilterById"](filterId.value);
  });

  const getFilterByLink = uri => {
    return computed(() => {
      return store.getters["filters/getFilterByLink"](uri);
    });
  };

  const allFilterRules = computed(() =>
    get(store, "state.filterRules.filterRules")
  );

  // NB: need to map "exclude" property if it is not present as it may
  // conflict with filterRules retrieved from the api
  const mapExclude = rule => {
    const r = { ...rule };
    if (r.exclude === undefined) {
      r.exclude = false;
    }
    return r;
  };

  const combination = computed(() => route.query.combination);

  const saveFilterIsActivated = computed(() => {
    const currentFilterRules = orderBy(
      currentFilter.value ? [...currentFilter.value.filterRules] : [],
      ({ type }) => {
        if (FILTER_RULES[type]) {
          return FILTER_RULES[type].weight;
        }
        return 100;
      }
    ).map(mapExclude);

    const appliedFilterRules = orderBy(
      [...allFilterRules.value],
      ({ type }) => {
        if (FILTER_RULES[type]) {
          return FILTER_RULES[type].weight;
        }
        return 100;
      }
    ).map(mapExclude);

    return (
      !deepEqual(currentFilterRules, appliedFilterRules) && !combination.value
    );
  });

  const isSaveFilterEnabled = computed(() => {
    if (currentFilter.value) {
      return !deepEqual(
        [...currentFilter.value.filterRules],
        [...allFilterRules.value]
      );
    }

    return allFilterRules.value.length > 0;
  });

  const combinedFilterRules = computed(() => {
    const combinedFilters =
      combination.value === "public"
        ? store.getters["settings/getPublicCombinedFilters"]
        : store.getters["settings/getUserCombinedFilters"];

    // @see: https://jira.mecom.de/browse/AGENDA-1193
    return combinedFilters
      ? combinedFilters.reduce(
          (acc, { _links }) => ({
            ...acc,
            values: [...acc.values, ...(_links ? [_links.self.href] : [])]
          }),
          {
            type: "FILTER_FILTER",
            values: []
          }
        )
      : {};
  });

  const setFilterRulesFromFilter = debounce(() => {
    // NB: filterRules are either from currentFilter or currentShortcut
    const filter = currentFilter.value;
    const filterRulesOfFilter = filter ? [...filter.filterRules] || [] : [];

    const shortcutTitle = computed(() => route.query.shortcut);
    const shortcut = SHORTCUTS.find(item => item.title === shortcutTitle.value);
    const filterRulesOfShortcut = shortcut
      ? [...shortcut.filterRules] || []
      : [];

    const useRulesOfFilter = filterRulesOfFilter.length > 0;
    const useRulesOfShortcut =
      !filterRulesOfFilter.length && filterRulesOfShortcut.length;
    let filterRules = [];
    if (useRulesOfFilter) filterRules = filterRulesOfFilter;
    if (useRulesOfShortcut) filterRules = filterRulesOfShortcut;

    store.commit("filterRules/setFilterRules", { filterRules });
  }, 0);

  const setFilterCombination = () => {
    let newFilterRules = allFilterRules.value;
    const rulesWithoutCombination = newFilterRules.filter(
      ({ type }) => type !== "FILTER_FILTER"
    );

    if (!combination.value) {
      newFilterRules = [...rulesWithoutCombination];
    } else {
      newFilterRules = [...rulesWithoutCombination, combinedFilterRules.value];
    }

    store.commit("filterRules/setFilterRules", { filterRules: newFilterRules });
  };

  watch(currentFilter, (newValue, oldValue) => {
    const filterAdded = !!newValue && !oldValue;
    const filterRemoved = !newValue && !!oldValue;
    const filterChanged =
      !!newValue && !!oldValue && newValue.id !== oldValue.id;

    // NB: Only set filterRules from the current filter under these conditions,
    // also fixes AGENDA-765
    if (filterAdded || filterRemoved || filterChanged) {
      setFilterRulesFromFilter();
    }
  });

  watch(combination, setFilterCombination);

  watch(combinedFilterRules, () => {
    if (combination.value !== undefined) {
      setFilterCombination();
    }
  });

  watch(allFilterRules, (newValue, oldValue) => {
    const filterCombination = newValue.find(
      ({ type }) => type === "FILTER_FILTER"
    );
    if (filterCombination && !deepEqual(newValue, oldValue)) {
      const combinedFilters = [
        ...(combination.value === "public"
          ? store.getters["settings/getUserCombinedFilters"]
          : store.getters["settings/getPublicCombinedFilters"]),
        ...filterCombination.values.map(link => {
          const id = link.split("/").pop();
          return store.getters["filters/getFilterById"](id);
        })
      ];
      store.commit("settings/setCombinedFilters", combinedFilters);
    }
  });

  return { saveFilterIsActivated, isSaveFilterEnabled, getFilterByLink };
}
